<template>
  <div
    ref="rootEl"
    class="bk bk-dialog bk-control"
    @wheel.passive.stop
    @keydown.stop="onKeyDown"
    @keyup.stop
    @touchstart.passive.stop
    @touchmove.stop
    @touchend.stop
  >
    <div class="bk-dialog-background" @click="$emit('cancel')" />
    <div class="bk-dialog-inner" :style="style">
      <div class="bk bk-overlay-header">
        <Icon v-if="icon" :name="icon" />
        <h3>{{ title }}</h3>
        <button @click="$emit('cancel')">
          <Icon name="close" />
        </button>
      </div>

      <div
        class="bk-dialog-content"
        :class="{
  'bk-is-fullscreen': fullScreen
}"
      >
        <div class="bk-dialog-content-inner">
          <div v-if="lead" class="bk bk-dialog-lead">
            {{ lead }}
          </div>
          <slot />
        </div>
      </div>
      <div v-if="!hideButtons" class="bk bk-dialog-footer">
        <button
          class="bk-button"
          :disabled="!canSubmit"
          :class="[
  { 'bk-is-loading': isLoading },
  isDanger ? 'bk-is-danger' : 'bk-is-primary'
]"
          @click="$emit('submit')"
        >
          {{ submitLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useBlokkli, onMounted, computed, ref } from "#imports";
import { Icon } from "#blokkli/components";
import { modulo } from "#blokkli/helpers";
const { ui } = useBlokkli();
const emit = defineEmits(["submit", "cancel"]);
const rootEl = ref(null);
const props = defineProps({
  title: { type: String, required: true },
  lead: { type: String, required: false, default: "" },
  width: { type: [Number, String], required: false, default: 600 },
  submitLabel: { type: String, required: false, default: "" },
  canSubmit: { type: Boolean, required: false, default: true },
  isDanger: { type: Boolean, required: false },
  isLoading: { type: Boolean, required: false },
  hideButtons: { type: Boolean, required: false },
  icon: { type: null, required: false, default: void 0 },
  fullScreen: { type: Boolean, required: false }
});
const style = computed(() => {
  if (ui.isMobile.value) {
    return {};
  }
  if (props.fullScreen) {
    return {
      maxWidth: "100vw",
      height: "100vh"
    };
  }
  if (typeof props.width === "number") {
    return {
      maxWidth: props.width + "px"
    };
  }
  return {
    width: props.width
  };
});
const getFocusableElements = () => {
  if (!rootEl.value) {
    return [];
  }
  return [
    ...rootEl.value.querySelectorAll("input,select,button,textarea")
  ];
};
const onKeyDown = (e) => {
  if (e.code === "Escape") {
    e.preventDefault();
    emit("cancel");
    return;
  }
  if (!rootEl.value || e.code !== "Tab") {
    return;
  }
  const prev = e.shiftKey;
  const focusableElements = getFocusableElements().filter((el) => {
    const style2 = window.getComputedStyle(el);
    if (style2.pointerEvents === "none") {
      return false;
    }
    return !el.disabled;
  });
  const activeIndex = Math.max(
    focusableElements.findIndex((el) => document.activeElement === el),
    0
  );
  const delta = prev ? -1 : 1;
  const indexToFocus = modulo(activeIndex + delta, focusableElements.length);
  const elementToFocus = focusableElements[indexToFocus];
  if (elementToFocus) {
    elementToFocus.focus();
    e.preventDefault();
  }
};
onMounted(() => {
  const focusableElements = getFocusableElements();
  const bestMatch = focusableElements.find((el) => !(el instanceof HTMLButtonElement)) || focusableElements[0];
  if (bestMatch) {
    bestMatch.focus();
  }
});
</script>

<script>
export default {
  name: "BlokkliDialog"
};
</script>
